import { useEffect, type RefObject } from 'react';
import type { ScrollView } from 'react-native';

type WebScrollView = ScrollView & {
  getScrollableNode?: () => HTMLElement | null;
};

export function useHorizontalMouseScroll(scrollRef: RefObject<ScrollView | null>): void {
  useEffect(() => {
    const node = (scrollRef.current as WebScrollView | null)?.getScrollableNode?.();
    if (!node) return undefined;

    let activePointerId: number | null = null;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let didDrag = false;
    const previousCursor = node.style.cursor;
    const previousUserSelect = node.style.userSelect;
    node.style.cursor = 'grab';

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const maximumScrollLeft = node.scrollWidth - node.clientWidth;
      const canScroll =
        (event.deltaY > 0 && node.scrollLeft < maximumScrollLeft) ||
        (event.deltaY < 0 && node.scrollLeft > 0);
      if (!canScroll) return;

      event.preventDefault();
      node.scrollLeft = Math.max(0, Math.min(maximumScrollLeft, node.scrollLeft + event.deltaY));
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      activePointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScrollLeft = node.scrollLeft;
      didDrag = false;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) return;
      const distance = event.clientX - dragStartX;
      if (!didDrag && Math.abs(distance) < 4) return;

      if (!didDrag) {
        didDrag = true;
        node.setPointerCapture(event.pointerId);
        node.style.cursor = 'grabbing';
        node.style.userSelect = 'none';
      }

      event.preventDefault();
      node.scrollLeft = dragStartScrollLeft - distance;
    };

    const finishDrag = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) return;
      if (node.hasPointerCapture(event.pointerId)) node.releasePointerCapture(event.pointerId);
      activePointerId = null;
      node.style.cursor = 'grab';
      node.style.userSelect = previousUserSelect;
    };

    const preventClickAfterDrag = (event: MouseEvent) => {
      if (!didDrag) return;
      event.preventDefault();
      event.stopPropagation();
      didDrag = false;
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    node.addEventListener('pointerdown', handlePointerDown);
    node.addEventListener('pointermove', handlePointerMove, { passive: false });
    node.addEventListener('pointerup', finishDrag);
    node.addEventListener('pointercancel', finishDrag);
    node.addEventListener('click', preventClickAfterDrag, true);

    return () => {
      node.removeEventListener('wheel', handleWheel);
      node.removeEventListener('pointerdown', handlePointerDown);
      node.removeEventListener('pointermove', handlePointerMove);
      node.removeEventListener('pointerup', finishDrag);
      node.removeEventListener('pointercancel', finishDrag);
      node.removeEventListener('click', preventClickAfterDrag, true);
      node.style.cursor = previousCursor;
      node.style.userSelect = previousUserSelect;
    };
  }, [scrollRef]);
}
